import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlImportDocTypes, urlParamReplicaId } from '../../Constants';
import { IAction, SlicePropsWithData } from '../store';
import { IProjectDocType } from '../project/sliceProjectDocTypes';
import { IExtendedConfig } from '../project/sliceProjectActions';
import { safeJsonParse } from '../../util/DataUtils';
import { apiFetch } from '../../util/ProjectUtils';

export interface IImportDocType extends IProjectDocType {
    isCategoryMandatory?: boolean;
    isPackageMandatory?: boolean;
    isAddressMandatory?: boolean;
    isKeywordMandatory?: boolean;
}

export type IImportDocTypesState = SlicePropsWithData<IImportDocType[]>;

const thunks = {
    _getImportDocTypes: async (replicaId: string): Promise<IImportDocType[]> => {
        try {
            const url = urlImportDocTypes.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url);

            if (!response.ok) {
                return Promise.reject(new Error(await response.text()));
            }

            const result = await response.json();
            const mappedResult = result.map(item => {
                return {
                    ...item,
                    extendedConfig: safeJsonParse<IExtendedConfig>(
                        item.extendedConfig as unknown as string,
                        null,
                    ),
                };
            });

            return mappedResult;
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    _reducers: (builder: ActionReducerMapBuilder<IImportDocTypesState>) => {
        builder.addCase(getImportDocTypes.pending, (state: Draft<IImportDocTypesState>) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            getImportDocTypes.fulfilled,
            (state: Draft<IImportDocTypesState>, action: IAction<IImportDocType[]>) => {
                state.data = action.payload;
                state.loading = false;
                state.error = null;
            },
        );
        builder.addCase(
            getImportDocTypes.rejected,
            (state: Draft<IImportDocTypesState>, action: IAction<SerializedError>) => {
                state.data = null;
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

export const getImportDocTypes = createAsyncThunk('import/doctypes', thunks._getImportDocTypes);

export const importDocTypesSlice = createSlice({
    name: 'importDocTypes',
    extraReducers: thunks._reducers,
    initialState: {
        data: [],
        loading: false,
        error: null,
    },
    reducers: {},
});

export default importDocTypesSlice.reducer;
